import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { ThemeProvider } from 'react-jss/lib';
import Header from 'components/Header';
import SideBar from 'components/SideBar';
import Footer from 'components/Footer';
import ScrollBar from 'react-scrollbars-custom';
import { withPermission } from '@mayo/user-permissions';
import { switchLocale } from 'containers/App/reducers/applicationReducer';
import { logout } from 'containers/App/reducers/userReducer';
import { url } from 'configs/';
import navigationConfig from './navigationConfig';
import './style.css';

const permissionConfig = {
  permission: 'tube',
  functionCodes: [
    // tube
    { functionCode: 'CompanyRuleEmp' },
    { functionCode: 'CompanyRuleHR' },
    { functionCode: 'DepartmentNewsEmp' },
    { functionCode: 'DepartmentNewsHR' },
    { functionCode: 'DepartmentNewsSup' },
    { functionCode: 'DepartmentNewsSec' },
    { functionCode: 'AnnouncementEmp' },
    { functionCode: 'AnnouncementHR' },
    { functionCode: 'OrderEmp' },
    { functionCode: 'OrderHR' },
    { functionCode: 'AssignmentTask' },
    { functionCode: 'Note' },
    { functionCode: 'InternalSystemLink' },
    { functionCode: 'Banner' },
    // ta
    { functionCode: 'PunchCard' },
    { functionCode: 'PersonalShiftSchedule' },
    { functionCode: 'SupervisorShiftScheduleApprovement' },
    { functionCode: 'PersonalShiftScheduleApprovementSec' },
    // py
    { functionCode: 'PersonalInfoSearchPrint' },
    // foundation
    { functionCode: 'MyDataInformation' },
    { functionCode: 'MyDataContact' },
    { functionCode: 'MyDataEducation' },
    { functionCode: 'MyDataCertificate' },
    { functionCode: 'MyDataWork' },
    { functionCode: 'MyDataYOS' },
    { functionCode: 'MyDataExperience' },
    { functionCode: 'MyTeam' },
    { functionCode: 'LinkUpAuthorityUi' },
    // gaia
    { functionCode: 'FormSigning' },
    { functionCode: 'FormApplication' },
    { functionCode: 'MyForm' },
    { functionCode: 'FormSearching' },
  ],
  baseURL: url.NEWTUBE_API_SERVER,
};

const mapStateToProps = (state) => {
  const { app } = state;
  return {
    application: app.application,
    user: app.user,
  };
};
const dispatchProps = {
  switchLocale,
};

function isAllow(permission, functionCode, actionCode = 'Default') {
  return (
    permission.filter(
      (p) => p.functionCode === functionCode && p.actionCode === actionCode
    ).length > 0
  );
}

function isItemAllow(item, permission) {
  if (item.functionCode === undefined) {
    return item;
  } else if (item.functionCode instanceof Array) {
    return item.functionCode.find((c) => isAllow(permission, c)) !== undefined
      ? item
      : undefined;
  }
  return isAllow(permission, item.functionCode) ? item : undefined;
}

function filterSideBarByPermission(item, permission) {
  if (item.items === undefined || item.items.length === 0) {
    return isItemAllow(item, permission);
  }
  const currentItemIsAllow = isItemAllow(item, permission);
  if (currentItemIsAllow) {
    const allowChildren = item.items
      .map((i) => filterSideBarByPermission(i, permission))
      .filter((i) => i !== undefined);
    if (allowChildren.length > 0) {
      return Object.assign({}, item, { items: allowChildren });
    }

    return undefined;
  }
  return undefined;
}

class Layout extends Component {
  static get propTypes() {
    return {
      application: PropTypes.shape({
        isLoggedIn: PropTypes.bool,
        locale: PropTypes.string,
        locales: PropTypes.array,
        progresses: PropTypes.array,
        showPrivacy: PropTypes.bool,
        theme: PropTypes.shape({
          companyLogo: PropTypes.string,
          headerColor: PropTypes.string,
        }),
      }).isRequired,
      user: PropTypes.shape({
        userDisplayName: PropTypes.string,
        allowModules: PropTypes.array,
        functionRole: PropTypes.object,
        isVerify: PropTypes.bool,
        isLoaded: PropTypes.bool,
        personalPicture: PropTypes.string,
        companyTimeZone: PropTypes.string,
      }).isRequired,
      history: PropTypes.object.isRequired,
      switchLocale: PropTypes.func.isRequired,
      children: PropTypes.node,
      main: PropTypes.node,
      sidebar: PropTypes.node,
      allPermissions: PropTypes.any,
    };
  }

  constructor() {
    super();
    this.state = {
      isSmallScreen: false,
      isSidebarShow: false,
      config: [],
    };
    this.handleSwitchLocale = this.handleSwitchLocale.bind(this);
    this.handleToggleSidebar = this.handleToggleSidebar.bind(this);
  }

  componentDidMount() {
    const { allPermissions } = this.props;
    const defaultConfig = navigationConfig
      .map((g) => {
        return Object.assign({}, g, {
          items: g.items
            .map((i) => filterSideBarByPermission(i, allPermissions))
            .filter((i) => i !== undefined),
        });
      })
      .filter((g) => g.items !== undefined && g.items.length !== 0);

    const mq = window.matchMedia('(max-width: 1300px)');
    /* eslint-disable react/no-did-mount-set-state */
    const listener = (mql) => {
      if (mql.matches && !this.state.isSmallScreen) {
        this.setState({ isSmallScreen: true, isSidebarShow: false });
      } else if (this.state.isSmallScreen) {
        this.setState({ isSmallScreen: false, isSidebarShow: false });
      }
    };
    mq.addListener(listener);
    this.setState({ isSmallScreen: mq.matches, config: defaultConfig });
  }

  handleToggleSidebar() {
    this.setState((prev) => ({ isSidebarShow: !prev.isSidebarShow }));
  }

  handleSwitchLocale(locale) {
    this.props.switchLocale(locale);
  }

  renderPreviewContent({ main, sidebar } = {}) {
    return (
      <div className="hn-preview">
        {sidebar}
        {main}
      </div>
    );
  }
  renderMainContent({ main, sidebar, application, user, history } = {}) {
    const { isSmallScreen, config, isSidebarShow } = this.state;
    const {
      isLoggedIn,
      isLoaded,
      isSidebarDisplay,
      locale,
      locales,
      showPrivacy,
      theme,
    } = application;
    const {
      allowModules,
      companyTimeZone,
      isLoaded: isUserLoaded,
      isVerify,
      personalPicture,
      timezone,
      userDisplayName,
    } = user;
    const isLoggedInLoaded = isLoggedIn && isLoaded && isUserLoaded && isVerify;
    let containMarginLeft = 'auto';
    if (isSidebarDisplay) {
      containMarginLeft = isSmallScreen ? '0' : '280px';
    }
    return (
      <ThemeProvider theme={theme}>
        <ScrollBar style={{ width: '100vw', height: '100vh' }} noScrollY>
          <Header
            isLoggedIn={isLoggedInLoaded}
            isSmallScreen={isSmallScreen}
            isSidebarDisplay={isSidebarDisplay}
            history={history}
            userName={userDisplayName}
            personalPicture={personalPicture}
            onLogout={logout}
            companyLogo={theme.companyLogo}
            primaryColor={theme.headerColor}
            locale={locale}
            locales={locales}
            onSwitchLocale={this.handleSwitchLocale}
            handleToggleSidebar={this.handleToggleSidebar}
            moduleAuth={allowModules}
            companyCode={user.companyCode}
            employeeNumber={user.employeeNumber}
          />
          <SideBar
            location={location}
            navigationConfig={config}
            isSmallScreen={isSmallScreen}
            isSidebarShow={isSidebarShow}
            isSidebarDisplay={isSidebarDisplay}
            companyLogo={theme.companyLogo}
            primaryColor={theme.headerColor}
            handleToggleSidebar={this.handleToggleSidebar}
          />
          <ScrollBar
            style={{
              height: 'calc(100vh - 70px)',
              width: '100vw',
              minWidth: '1200px',
            }}
            noScrollX={isSmallScreen}
          >
            <div
              className="apollo-layout-content"
              style={{ marginLeft: containMarginLeft }}
            >
              {sidebar}
              {main}
            </div>
            <Footer
              locale={locale}
              locales={locales}
              onSwitchLocale={this.handleSwitchLocale}
              showPrivacy={showPrivacy}
              isSidebarDisplay={isSidebarDisplay}
              isSmallScreen={isSmallScreen}
              companyTimeZone={companyTimeZone}
              timezone={timezone}
            />
          </ScrollBar>
        </ScrollBar>
      </ThemeProvider>
    );
  }

  render() {
    const { props } = this;
    const { application, user, history, location } = props;
    const { isPreviewMode } = application;
    let main = props.main;
    const sidebar = props.sidebar;
    if (!sidebar) {
      main = props.children;
    }
    return (
      <div>
        {(() => {
          if (!application.isLoaded) {
            return <span />;
          }

          return isPreviewMode
            ? this.renderPreviewContent({ main, sidebar })
            : this.renderMainContent({
              main,
              sidebar,
              application,
              user,
              history,
              location,
            });
        })()}
      </div>
    );
  }
}

export default compose(
  withPermission(permissionConfig),
  connect(mapStateToProps, dispatchProps)
)(Layout);



/** WEBPACK FOOTER **
 ** D:/a/1/s/src/containers/App/components/Layout/index.js
 **/